/**
 * Central onboarding gate — decides whether the profile wizard must run.
 * Handles legacy users who filled user_profiles before onboarding_completed existed.
 */

import type { UserProfile } from '../../types/userProfile';

export function hasLegacyOnboardingBypass(): boolean {
  try {
    if (localStorage.getItem('onboarding_permanently_disabled') === 'true') return true;
  } catch {
    /* ignore */
  }
  return false;
}

/** Profile has enough data that re-running the wizard is redundant. */
export function hasSubstantiveProfile(profile: UserProfile | null | undefined): boolean {
  if (!profile) return false;

  const hasName = Boolean(profile.displayName?.trim());
  const hasRole = Boolean(profile.currentRole?.trim());
  const hasYears = profile.experienceYears != null && profile.experienceYears > 0;
  const hasSkills = (profile.skills?.length ?? 0) > 0;
  const hasWorkHistory = (profile.experiences?.length ?? 0) > 0;
  const hasResume = Boolean(profile.resumeRawText?.trim() || profile.resumeFileUrl);

  return hasName && hasRole && (hasYears || hasSkills || hasWorkHistory || hasResume);
}

export function isOnboardingComplete(
  profile: UserProfile | null | undefined,
  prefs?: { onboarding_complete?: boolean } | null,
): boolean {
  if (hasLegacyOnboardingBypass()) return true;
  if (profile?.onboardingCompleted) return true;
  if (prefs?.onboarding_complete) return true;
  if (hasSubstantiveProfile(profile)) return true;
  return false;
}

/** True when DB flag is false but profile data implies onboarding was already done. */
export function shouldBackfillOnboardingFlag(profile: UserProfile | null | undefined): boolean {
  if (!profile) return false;
  if (profile.onboardingCompleted) return false;
  return hasSubstantiveProfile(profile);
}
